// useSockets.js
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';

export default function useSockets({
  sites = [],
  urlBuilder,
  ioOptions = {},
  connectTimeoutMs = 10000,
  voice = false,
  voiceCallbacks: initialVoiceCallbacks = {},
  onSocketsUpdate, // optional callback receiving shallow copy of sockets map when updated
  onEventsUpdate, // optional callback(site, eventsArray)
} = {}) {
  const socketsRef = useRef({});           // { [site]: Socket }
  const pendingWaitRef = useRef({});       // { [site]: Promise<Socket> }
  const voiceHandlersAttachedRef = useRef({}); // { [site]: boolean }
  const voiceCallbacksRef = useRef(initialVoiceCallbacks || {});
  const voicePeerMetaRef = useRef({
    peerId: '',
    adminName: '',
    adminId: '',
    remotePeerID: '',
    reconnect: true,
  });
  const lastServiceSignatureRef = useRef(null); // prevent duplicate identical serviceAdminSocket emits
  const siteEventsRef = useRef({}); // { [site]: eventsArray }

  const sitesList = useMemo(
    () => Array.from(new Set(sites || [])),
    [sites?.join(',')]
  );

  const buildUrl =
    urlBuilder ||
    ((site) => (site === 'localhost'
      ? 'http://localhost:8080'
      : `https://${site}.carteav.com`)
    );




  // connect to all sites
  useEffect(() => {
    if (!sites || !sites.length) return;

    let changed = false;
    sites.forEach((site_name) => {
      const url = buildUrl(site_name);

      let socket = socketsRef.current[site_name];
      console.log(socket)
      if (!socket) {
        socket = io(url, {
          secure: true,
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 500,
          reconnectionDelayMax:2000,
          ...ioOptions,

        });
        
        socketsRef.current[site_name] = socket;
        changed = true;
      }

      const onConnect = () => {
        console.log(`Connected to ${site_name} at ${url}`);
        socket.emit('registerToReceiveEvents');
      };

      const onDisconnect = (reason) => {
        console.log(`Disconnected from ${site_name}: ${reason}`);
      };

      /** MANAGER:
       **/
      const m = socket.io; // Manager

      const onReconnectAttempt = (attempt) =>
        console.log(`[${site_name}] reconnect_attempt #${attempt}`);

      const onReconnectError = (err) =>
        console.log(`[${site_name}] reconnect_error:`, err?.message || err);

      const onReconnectFailed = () =>
        console.log(`[${site_name}] reconnect_failed (attempts exhausted)`);

      const onReconnected = (attempt) =>
        console.log(
          `[${site_name}] reconnected after ${attempt} attempt(s), socket.id=${socket.id}`
        );

      const onOpen = () =>
        console.log(`[${site_name}] manager open (transport=${m.engine?.transport?.name})`);

      const onClose = (reason) =>
        console.log(`[${site_name}] manager close: ${reason}`);

      const onConnectError = (err) =>
        console.log(`[${site_name}] connect_error:`, err?.message || err);

      const onPing = () => console.log(`[${site_name}] ping received -- > Sending Pong to ${site_name} [arrival to server is not guarranteed]`);
      const onPong = (latency) => console.log(`[${site_name}] pong (${latency} ms)`);
      m.off('ping', onPing).on('ping', onPing);
      m.off('pong', onPong).on('pong', onPong);

      m.off('open', onOpen).on('open', () =>
        console.log(`[${site_name}] transport=${m.engine?.transport?.name}`)
      );
      m.off('close', onClose).on('close', (reason) =>
        console.log(`[${site_name}] manager close: ${reason}`)
      );

      // socket.onAny((event, ...args) => {
      //   // Uncomment for debugging all events
      //   console.warn(new Date().toISOString(), `[SOCKET -- ${site_name}] event: ${event}`, args);
      // });


      // const eio = m.engine;
      // eio.on("ping", () => {
      //   console.log(new Date().toISOString(), `[EIO -- ${site_name}]  ping received`);
      // });


      // eio.on("pong", () => {
      //   console.log(new Date().toISOString(), `[EIO client] pong sent to ${site_name}`);
      // });


      m.on('reconnect_attempt', onReconnectAttempt);
      m.on('reconnect_error', onReconnectError);
      m.on('reconnect_failed', onReconnectFailed);
      m.on('reconnect', onReconnected);
      m.on('open', onOpen);
      m.on('close', onClose);
      socket.on('connect_error', onConnectError);

      /* MANAGER END 
      */


      socket.off('connect', onConnect).on('connect', onConnect);
      socket.off('disconnect', onDisconnect).on('disconnect', onDisconnect);

      // centralized receiveEvents listener (incremental per site)
      const receiveEventsHandler = (data, ack) => {
        const safeAck = (ok = true) => { try { typeof ack === "function" && ack(ok); } catch {} };

        try {
          // Always replace site events (no reference equality check)
          siteEventsRef.current[site_name] = data;
          if (typeof onEventsUpdate === 'function') onEventsUpdate(site_name, data);

          safeAck(true);   // tell server we're done processing
        } catch (e) {
          console.error("receiveEventsHandler failed:", e);
          safeAck(false);  // let server know it failed (so it can retry/skip)
        }
      };

      socket.off('receiveEvents', receiveEventsHandler).on('receiveEvents', receiveEventsHandler);



      if (voice && !voiceHandlersAttachedRef.current[site_name]) {
        attachVoiceHandlers(socket, site_name);
        voiceHandlersAttachedRef.current[site_name] = true;
      }

    });

    if (changed && typeof onSocketsUpdate === 'function') {
      onSocketsUpdate({ ...socketsRef.current });
    }

    return () => {
      // tear down sockets that are no longer in `sites`
      Object.entries(socketsRef.current).forEach(([site, s]) => {
        if (s && !sites.includes(site)) {
          s.disconnect();
          delete socketsRef.current[site];
          delete pendingWaitRef.current[site];
          delete voiceHandlersAttachedRef.current[site];
          delete siteEventsRef.current[site];
          changed = true;
        }
      });
      if (changed && typeof onSocketsUpdate === 'function') {
        onSocketsUpdate({ ...socketsRef.current });
      }
      if (changed && typeof onEventsUpdate === 'function') {
        // notify removals for any deleted sites (pass undefined data)
        Object.keys(siteEventsRef.current).forEach((existingSite) => {
          if (!sites.includes(existingSite)) {
            onEventsUpdate(existingSite, undefined);
          }
        });
      }
    };
  }, [sitesList]);

  // Internal helper: wait until a socket is connected
  const waitUntilConnected = useCallback((site, timeoutMs) => {
    const socket = socketsRef.current[site];
    if (!socket) {
      return Promise.reject(new Error(`No socket for site "${site}" (not in sites list yet?)`));
    }
    if (socket.connected) return Promise.resolve(socket);

    // De-duplicate concurrent waiters for the same site
    if (pendingWaitRef.current[site]) return pendingWaitRef.current[site];

    pendingWaitRef.current[site] = new Promise((resolve, reject) => {
      let done = false;
      const finish = (fn, arg) => {
        if (done) return;
        done = true;
        // cleanup listeners and pending entry
        socket.off('connect', onConnect);
        socket.off('connect_error', onError);
        socket.off('reconnect_failed', onError);
        clearTimeout(tid);
        delete pendingWaitRef.current[site];
        fn(arg);
      };

      const onConnect = () => finish(resolve, socket);
      const onError = (err) => finish(reject, err instanceof Error ? err : new Error(String(err)));
      const tid = setTimeout(() => finish(reject, new Error(`Timed out waiting for "${site}" to connect`)), timeoutMs ?? connectTimeoutMs);

      socket.once('connect', onConnect);
      socket.once('connect_error', onError);
      socket.once('reconnect_failed', onError);
    });

    return pendingWaitRef.current[site];
  }, [connectTimeoutMs]);

  // Async getter: resolves only when connected
  const getSocket = useCallback(async (site, opts = {}) => {
    const { timeoutMs } = opts;
    // ensure the site exists in the ref (useEffect will create it)
    if (!socketsRef.current[site]) {
      // If the caller asks for a site not in the list, fail fast
      if (!sitesList.includes(site)) {
        throw new Error(`Site "${site}" is not in the current sites list`);
      }
      // Otherwise, wait a tick so useEffect can create it this render
      await Promise.resolve();
    }
    return waitUntilConnected(site, timeoutMs);
  }, [sitesList, waitUntilConnected]);

  const emitAll = useCallback((event, payload, ack) => {
    Object.values(socketsRef.current).forEach((s) => s?.emit?.(event, payload, ack));
  }, []);

  /* ========================= VOICE LAYER ============================= */
  function attachVoiceHandlers(socket, siteName) {
    // initial emits
    socket.emit('adminAvailability', true);
    console.log(voicePeerMetaRef.current)
    // if (voicePeerMetaRef.current.peerId) {
    //   socket.emit('serviceAdminSocket', [
    //     voicePeerMetaRef.current.peerId,
    //     voicePeerMetaRef.current.adminName,
    //     voicePeerMetaRef.current.adminId,
    //     voicePeerMetaRef.current.remotePeerID,
    //     voicePeerMetaRef.current.reconnect,
    //   ]);
    // }

    socket.on('close_call', () => voiceCallbacksRef.current.onCloseCall?.());
    socket.on('getAdminService', () => {
      console.log("getAdminService", siteName, socket);
      // Don't forcibly re-register; just pass through so UI can react.
      voiceCallbacksRef.current.onGetAdminService?.(siteName, socket);
    });
    socket.on('callUserDetails', (data) => voiceCallbacksRef.current.onCallUserDetails?.(data, siteName, socket));
    socket.on('userCancelVoiceCall', (data) => voiceCallbacksRef.current.onUserCancelVoiceCall?.(data));
    socket.on('anotherAdminHandleTheVoiceCall', () => voiceCallbacksRef.current.onAnotherAdminHandleTheVoiceCall?.());
    socket.on('peer_unavailable', () => voiceCallbacksRef.current.onPeerUnavailable?.());
    socket.on('getUserDetails', (arr) => voiceCallbacksRef.current.onGetUserDetails?.(arr, siteName, socket));
    socket.on('disconnect', () => voiceCallbacksRef.current.onDisconnect?.(siteName));
    socket.on('user_disconnected', () => voiceCallbacksRef.current.onUserDisconnected?.());
  }

  const registerVoiceCallbacks = useCallback((callbacks = {}) => {
    voiceCallbacksRef.current = { ...voiceCallbacksRef.current, ...callbacks };
  }, []);

  const setVoicePeerMeta = useCallback((meta = {}) => {
    voicePeerMetaRef.current = { ...voicePeerMetaRef.current, ...meta };
    // Note: emitServiceAdminSocketAll() removed to prevent duplicate emissions
    // The server will call getAdminService which triggers handleGetAdminService instead
  }, []);

  const sendAdminAvailability = useCallback((isAvailable) => {
    Object.values(socketsRef.current).forEach((s) => s?.emit?.('adminAvailability', isAvailable));
  }, []);

  const updateRemotePeerID = useCallback((remotePeerID) => {
    voicePeerMetaRef.current.remotePeerID = remotePeerID || '';
    Object.values(socketsRef.current).forEach((s) => s?.emit?.('updateRemotePeerID', voicePeerMetaRef.current.remotePeerID));
  }, []);

  const emitServiceAdminSocketAll = useCallback(() => {
    const { peerId, adminName, adminId, remotePeerID, reconnect } = voicePeerMetaRef.current;
    if (!peerId || !adminName || !adminId) return;
    const payload = [peerId, adminName, adminId, remotePeerID || '', !!reconnect];
    const signature = JSON.stringify(payload);
    if (lastServiceSignatureRef.current === signature) return; // skip duplicate
    lastServiceSignatureRef.current = signature;
    console.log(`SERVICE ADMIN SOCKET SENT FROM useSockets: `, payload)
    Object.values(socketsRef.current).forEach((s) => s?.emit?.('serviceAdminSocket', payload));
  }, []);

  const voiceSocketsApi = voice ? {
    registerCallbacks: registerVoiceCallbacks,
    setPeerMeta: setVoicePeerMeta,
    sendAdminAvailability,
    updateRemotePeerID,
    emitServiceAdminSocketAll,
    getAllSockets: () => socketsRef.current,
  } : undefined;

  return {
    // async: resolves to a connected socket for the site (or rejects on timeout)
    getSocket,
    emitAll,
    sites,
    sockets: socketsRef.current,
    siteEvents: siteEventsRef.current,
    voiceSocketsApi,
  };
}
